/**
 * useNotifications — dual-mode, matching this codebase's established
 * pattern elsewhere (demo: local seed; real: TanStack Query against the
 * real API). NotificationsBell (shell/NotificationsDrawer.tsx) previously
 * read straight off the seed store in every mode — the real server-side
 * notification API (server/routers/notification.js) already exists and
 * works, it just had no client caller at all, so a notification created
 * server-side (e.g. a rejected/approved project-creation request) was
 * invisible until a full page reload re-hydrated the seed from scratch.
 *
 * refetchOnWindowFocus + a 60s refetchInterval stand in for a live push
 * channel this app doesn't have — cheap enough for a handful of rows,
 * and covers the common case (switching back to the tab) plus a backstop.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, demoModeActive } from "./api";
import { useSeed, useMutate } from "./hooks";
import {
  markNotificationRead as markNotificationReadDemo,
  markAllNotificationsRead as markAllNotificationsReadDemo,
} from "./mutations";
import type { Notification } from "./types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapApiNotification(n: any): Notification {
  return {
    id: String(n.id),
    // Not returned by the server and purely cosmetic on this type (no
    // consumer switches on it) — "system" is a safe, honest default.
    kind: "system",
    title: n.title ?? "",
    body: n.message ?? "",
    timestamp: n.createdat ?? new Date().toISOString(),
    read: Boolean(n.isread),
    href: n.link ?? undefined,
  };
}

export function useNotifications(): {
  notifications: Notification[];
  unreadCount: number;
  markRead: (id: string) => void;
  markAllRead: () => void;
} {
  const seed = useSeed();
  const mutate = useMutate();
  const queryClient = useQueryClient();
  const demo = demoModeActive();

  const { data: apiData } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.getNotifications({ limit: 20 }),
    enabled: !demo,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });

  if (demo) {
    return {
      notifications: seed.notifications,
      unreadCount: seed.notifications.filter((n) => !n.read).length,
      markRead: (id) => mutate((s) => markNotificationReadDemo(s, id)),
      markAllRead: () => mutate((s) => markAllNotificationsReadDemo(s)),
    };
  }

  const notifications = ((apiData as unknown[]) ?? []).map(mapApiNotification);
  return {
    notifications,
    unreadCount: notifications.filter((n) => !n.read).length,
    markRead: (id) => {
      void api.markNotificationRead(id).then(() => {
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
      });
    },
    markAllRead: () => {
      void api.markAllNotificationsRead().then(() => {
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
      });
    },
  };
}
